import s from './Sidebar.module.scss';
import { FC } from 'react';
import { Button, Card, Flex } from '@tremor/react';
import { useMatches, useSearchParams } from 'react-router-dom';
import { chain } from 'lodash';
import { SidebarLinkConditional } from '@/types/Sidebar';
import { permissions, UserRole } from '@/types/types';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { IconLink } from '../Links/IconLink/IconLink';
import useSidebarStore from '@/stores/sidebarStore';
import { SidebarRootLink } from '../Links/IconLink/SidebarRootLink';
import { getSidebarUrlsMapFunction } from '@/routes/navigation';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  links: SidebarLinkConditional[];
  bottomLinks: SidebarLinkConditional[];
}

export const MAIN_PAGE_LINK_TEXT = 'keywords management';

export function checkLinkPermissionValidity(
  workspacePermission: UserRole | null,
  organizationPermission: UserRole | null,
  subscriptionTier?: string | null,
  isInOrganization?: boolean,
) {
  return (link: SidebarLinkConditional) => {
    let isPermissionValid = true;
    let isCustomRuleValid = true;
    let isSubscriptionValid = true;
    if (link.permission) {
      isPermissionValid =
        link.permissiontType === 'organization'
          ? permissions[organizationPermission as keyof typeof permissions] >=
            permissions[link.permission]
          : permissions[workspacePermission as keyof typeof permissions] >=
            permissions[link.permission];
    }
    if (link.neededSubscriptionTier) {
      isSubscriptionValid = link.neededSubscriptionTier === subscriptionTier;
    }
    if (link.customRule) {
      isCustomRuleValid = !link.customRule({
        isWorkspaceAdmin: workspacePermission === 'admin',
        isInOrganization: !!isInOrganization,
      });
    }
    return {
      permissionGranted: isPermissionValid && isCustomRuleValid && isSubscriptionValid,
      isPermissionValid,
      isCustomRuleValid,
      isSubscriptionValid,
    } as const;
  };
}

export const Sidebar: FC<Props> = ({ links, bottomLinks }) => {
  const isExpanded = useSidebarStore((state) => state.isOpen);
  const toggleSidebar = useSidebarStore((state) => state.toggle);
  const [searchParams] = useSearchParams();
  const organizationPermission = useUserPermissionsStore(
    (state) => state.organizationPermission,
  );
  const workspacePermission = useUserPermissionsStore(
    (state) => state.workspacePermission,
  );
  const selectedOrganization = useOrganizationStore(
    (state) => state.selectedOrganization,
  );
  const func = checkLinkPermissionValidity(
    workspacePermission?.role as UserRole,
    organizationPermission?.role as UserRole,
    selectedOrganization?.subscriptionTier,
    selectedOrganization?.isOrganizationMember,
  );
  const sanitizedLinks = chain(links).map(getSidebarUrlsMapFunction(func)).value();

  const sanitizedBottomLinks = chain(bottomLinks)
    .map(getSidebarUrlsMapFunction(func))
    .value();

  // Adding this prevents blinking of 'no permission' links states
  let arePermissionsFetched =
    !!workspacePermission &&
    (!!organizationPermission || !selectedOrganization?.isOrganizationMember);

  // resolves impossible state when user is not a member of organization, but has organization permission
  if (!!organizationPermission && !selectedOrganization?.isOrganizationMember) {
    arePermissionsFetched = false;
  }

  return (
    <Card
      className={[
        s.sidebarContainer,
        isExpanded ? s.sidebarExpanded : s.sidebarShrinked,
      ].join(' ')}
    >
      <Flex
        justifyContent="start"
        flexDirection="col"
        alignItems="start"
        className="h-full"
      >
        <div className={clsx('w-full', isExpanded ? 'px-[24px]' : 'px-[8px]')}>
          <Button
            className="w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors group relative  my-6 bg-transparent text-black outline-none shadow-none h-10 border-0 [&>p]:flex [&>p]:flex-row [&>p]:justify-end [&>p]:gap-2 [&>p]:w-full hover:bg-gray-50 focus:outline-none focus:border-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-primary-400"
            onClick={toggleSidebar}
          >
            {isExpanded ? <ChevronLeft color="black" /> : <ChevronRight color="black" />}
          </Button>
        </div>
        <hr
          style={{
            width: '100%',
            border: 'none',
            backgroundColor: '#CFCFD1',
            height: '1px',
            marginBottom: '6px',
          }}
        />
        <div className={clsx('w-full', isExpanded ? 'px-[24px]' : 'px-[8px]')}>
          {arePermissionsFetched
            ? sanitizedLinks.map((link) => {
                const key = link.originalLink
                  ? `${link.originalLink}-${link.to}`
                  : link.to;
                return (
                  <div key={key}>
                    <IconLink
                      disabled={link.disabled}
                      isExpanded={isExpanded}
                      icon={link.icon}
                      iconAltText={link.altIconText}
                      to={{ pathname: link.to, search: searchParams.toString() }}
                      activeClassName={s.activeLink}
                      end={link.text === MAIN_PAGE_LINK_TEXT}
                      tooltip={isExpanded ? undefined : link.text}
                    >
                      {link.text}
                    </IconLink>
                    {link.sublinks ? (
                      <SublinksManager
                        isOrganizationMember={
                          !!selectedOrganization?.isOrganizationMember
                        }
                        organizationRole={organizationPermission?.role as UserRole}
                        workspaceRole={workspacePermission?.role as UserRole}
                        isExpanded={isExpanded}
                        links={link.sublinks}
                        baseLink={link.sublinksRoot ? link.sublinksRoot : link.to}
                        subscriptionTier={selectedOrganization?.subscriptionTier}
                      />
                    ) : null}
                  </div>
                );
              })
            : null}
        </div>
        <div className={clsx('w-full mt-auto', isExpanded ? 'px-[24px]' : 'px-[8px]')}>
          {arePermissionsFetched
            ? sanitizedBottomLinks.map((link) => {
                return (
                  <SidebarRootLink
                    key={link.to}
                    isExpanded={isExpanded}
                    to={link.to}
                    isDropdownMode={link.isDropdownMode}
                    icon={link.icon}
                    altIconText={link.altIconText}
                    additionalTo={link.additionalTo}
                    workspaceRole={workspacePermission?.role as UserRole}
                    organizationRole={organizationPermission?.role as UserRole}
                    isOrganizationMember={!!selectedOrganization?.isOrganizationMember}
                    text={link.text}
                    sublinks={link.sublinks}
                    sublinksRoot={link.sublinksRoot}
                  />
                );
              })
            : null}
        </div>
      </Flex>
    </Card>
  );
};

export interface SublinksManagerProps {
  links: SidebarLinkConditional[];
  baseLink: string;
  isExpanded: boolean;
  workspaceRole: UserRole;
  organizationRole: UserRole;
  isOrganizationMember: boolean;
  isDropDownMode?: boolean;
  isSelected?: boolean;
  subscriptionTier?: string | null;
}

export function SublinksManager({
  baseLink,
  links,
  isExpanded,
  isOrganizationMember,
  organizationRole,
  workspaceRole,
  isDropDownMode,
  isSelected,
  subscriptionTier,
}: SublinksManagerProps) {
  const [searchParams] = useSearchParams();
  const matches = useMatches();
  const isPathMatch = matches.some(({ pathname }) => pathname === baseLink);
  let isActive = isPathMatch;
  if (isDropDownMode) {
    isActive = !!isSelected && !!isDropDownMode;
  }
  const func = checkLinkPermissionValidity(
    workspaceRole,
    organizationRole,
    subscriptionTier,
    isOrganizationMember,
  );
  const sanitizedLinks = chain(links).map(getSidebarUrlsMapFunction(func)).value();
  return isActive ? (
    <>
      {sanitizedLinks.map((link) => {
        const key = link.originalLink ? `${link.originalLink}-${link.to}` : link.to;
        return (
          <IconLink
            disabled={link.disabled}
            additionalTo={link.additionalTo}
            isSublink={true}
            isExpanded={isExpanded}
            key={key}
            icon={link.icon}
            iconAltText={link.altIconText}
            to={{ pathname: link.to, search: searchParams.toString() }}
            // activeClassName={s.activeLink}
            end={link.text === MAIN_PAGE_LINK_TEXT}
            tooltip={isExpanded ? undefined : link.text}
          >
            {link.text}
          </IconLink>
        );
      })}
    </>
  ) : null;
}
